/**
 * A QR code, drawn as SVG rectangles.
 *
 * **Rendered from the module matrix rather than injected as markup.** `qrcode-generator`
 * offers `createSvgTag()`, which returns a string — using it would mean
 * `dangerouslySetInnerHTML` on a page that displays a TOTP secret, and the application's
 * content-security-policy exists precisely so that no code path does that. Reading
 * `isDark(row, col)` and emitting `<rect>` elements costs a few lines and keeps the
 * rendering path free of raw HTML.
 *
 * `qrcode-generator` was chosen over `qrcode` because it has no dependencies at all;
 * `qrcode` pulls in yargs and dijkstrajs to support a command line this application does
 * not use.
 */

import { useMemo, type ReactElement } from 'react';
import qrcode from 'qrcode-generator';

export interface QrCodeProps {
  /** The text to encode. For enrolment this is the `otpauth://` provisioning URI. */
  value: string;
  /** Rendered edge length in CSS pixels. */
  size?: number;
  /** Accessible description. The value itself is never announced — it is a secret. */
  label?: string;
}

/**
 * Render `value` as a scannable QR code.
 *
 * @param props - The value and presentation options.
 * @returns The SVG.
 */
export function QrCode({ value, size = 200, label = 'QR code' }: QrCodeProps): ReactElement {
  const { modules, count } = useMemo(() => {
    // Type 0 selects the smallest version that fits. Error correction `M` is what
    // authenticator apps expect and tolerates the modest smudging of a screen photo.
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();
    const n = qr.getModuleCount();
    const dark: Array<[number, number]> = [];
    for (let row = 0; row < n; row += 1) {
      for (let col = 0; col < n; col += 1) {
        if (qr.isDark(row, col)) dark.push([row, col]);
      }
    }
    return { modules: dark, count: n };
  }, [value]);

  // A four-module quiet zone, which the specification requires; scanners fail without it.
  const quiet = 4;
  const extent = count + quiet * 2;

  return (
    <svg
      data-testid="mfa-qr"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${extent} ${extent}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      className="rounded bg-white p-1"
    >
      {/* White throughout, not `currentColor`: a scanner needs the contrast whatever
          theme the page is in, and a dark-mode QR code on a dark background does not
          scan. */}
      <rect width={extent} height={extent} fill="#ffffff" />
      {modules.map(([row, col]) => (
        <rect
          key={`${row}-${col}`}
          x={col + quiet}
          y={row + quiet}
          width={1}
          height={1}
          fill="#000000"
        />
      ))}
    </svg>
  );
}
